import { useCallback, useEffect, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import {
  fetchDownstreamOrphans,
  importDownstreamOrphan,
  type DownstreamOrphan,
} from '@frontend/api-clients/adminProjectOrphansClient';
import type { EcosystemAdminEntry } from '@frontend/types';

export interface DownstreamOrphansPanelProps {
  ecosystems: EcosystemAdminEntry[];
  onImported?: () => void;
}

export default function DownstreamOrphansPanel({
  ecosystems,
  onImported,
}: DownstreamOrphansPanelProps) {
  const [orphans, setOrphans] = useState<DownstreamOrphan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importingId, setImportingId] = useState<string | null>(null);
  const [ecosystemByProject, setEcosystemByProject] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');

  const activeEcosystems = ecosystems.filter((e) => !e.decommissioned);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const token = authClient.getToken();
      if (!token) return;
      const res = await fetchDownstreamOrphans(token);
      setOrphans(res.orphans || []);
      setEcosystemByProject((prev) => {
        const next = { ...prev };
        const fallback = ecosystems.find((e) => !e.decommissioned)?.id || '';
        for (const o of res.orphans || []) {
          if (!next[o.projectId] && fallback) next[o.projectId] = fallback;
        }
        return next;
      });
    } catch (e) {
      setOrphans([]);
      setError(e instanceof Error ? e.message : 'Failed to load downstream orphans');
    } finally {
      setLoading(false);
    }
  }, [ecosystems]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRegister = async (orphan: DownstreamOrphan) => {
    const ecosystemId = ecosystemByProject[orphan.projectId] || activeEcosystems[0]?.id;
    if (!ecosystemId) {
      setError('Select an ecosystem before registering');
      return;
    }
    setImportingId(orphan.projectId);
    setError('');
    setMessage('');
    try {
      const token = authClient.getToken();
      if (!token) return;
      await importDownstreamOrphan(token, {
        projectId: orphan.projectId,
        ecosystemId,
        reconcile: false,
      });
      setMessage(`Registered ${orphan.projectId} in ${ecosystemId}`);
      await load();
      onImported?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImportingId(null);
    }
  };

  return (
    <div className="admin-orch-orphans">
      <div className="admin-orch-section-header">
        <h4 className="admin-orch-subtitle">Downstream orphans</h4>
        <button type="button" className="btn btn-secondary" onClick={() => void load()} disabled={loading}>
          Refresh
        </button>
      </div>
      <p className="ecosystem-description">
        OpenProject projects that are not yet in the portal catalog. Register one to assign an
        ecosystem and make it visible in Realm Admin.
      </p>

      {loading ? <p className="ecosystem-description">Scanning OpenProject…</p> : null}
      {error ? (
        <div className="settings-message settings-error" role="alert">
          {error}
        </div>
      ) : null}
      {message ? (
        <div className="settings-message settings-success" role="status">
          {message}
        </div>
      ) : null}

      {!loading && !error && orphans.length === 0 ? (
        <p className="ecosystem-description">No downstream orphans detected.</p>
      ) : null}

      {orphans.length > 0 ? (
        <ul className="admin-orch-orphan-list">
          {orphans.map((o) => (
            <li key={o.projectId} className="admin-orch-orphan-row">
              <div className="admin-orch-orphan-main">
                <strong>{o.name || o.projectId}</strong>
                <code className="admin-inline-code">{o.projectId}</code>
              </div>
              <div className="admin-orch-orphan-actions">
                <label className="admin-orch-orphan-eco-label">
                  Ecosystem
                  <select
                    value={ecosystemByProject[o.projectId] || activeEcosystems[0]?.id || ''}
                    onChange={(e) =>
                      setEcosystemByProject((prev) => ({
                        ...prev,
                        [o.projectId]: e.target.value,
                      }))
                    }
                  >
                    {activeEcosystems.map((eco) => (
                      <option key={eco.id} value={eco.id}>
                        {eco.displayName || eco.id}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={importingId === o.projectId || activeEcosystems.length === 0}
                  onClick={() => void onRegister(o)}
                >
                  {importingId === o.projectId ? 'Registering…' : 'Register in portal'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
